import {format} from 'date-fns';
import {useComponentsTheme} from 'platform/components';
import {Box, Show, Text} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {CSSProperties} from 'react';

import {isNotNil} from 'ramda';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EVENT_FILTER_WHEN_IS_PAST} from '../constants/eventFilterWhenIsPast';
import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import {formatEventTimeSpan} from '../utils/formatEventTimeSpan';
import {isMultiDayEvent} from '../utils/isMultiDayEvent';
import {isPastEvent} from '../utils/isPastEvent';
import {repeatingGradient} from '../utils/repeatingGradient';
import {resolveCalendarColor} from '../utils/resolveCalendarColor';

interface MonthEventItemProps extends RequiredTestIdProps {
  event: BigCalendarEvent;
  /** The event continues from the previous week row, so the left edge stays square. */
  isStartClipped?: boolean;
  /** The event continues into the next week row, so the right edge stays square. */
  isEndClipped?: boolean;
  onClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onMouseLeave?: (event: BigCalendarEvent) => void;
}

type MonthEventStyle = CSSProperties & {
  '--event-filter': string;
};

const monthEventVariants = cva(
  'flex h-full min-w-0 cursor-pointer flex-row gap-1 overflow-hidden px-1 py-[2px] [transition:filter_0.2s_ease]',
  {
    variants: {
      enableHover: {
        true: 'hover:[filter:brightness(0.85)_var(--event-filter)]',
        false: '',
      },
    },
  }
);

export function MonthEventItem(props: MonthEventItemProps) {
  const theme = useComponentsTheme();
  const eventColorHex = resolveCalendarColor(props.event.color || 'general.black', theme);

  const isMultiDay = isMultiDayEvent(props.event.start, props.event.end);

  // An all-day event has no time to state; a multi-day one needs both boundaries, matching the
  // bar in the all-day header; a single-day one only needs the time it starts at.
  const timeLabel = match({isAllDay: props.event.isAllDay === true, isMultiDay})
    .with({isAllDay: true}, () => null)
    .with({isMultiDay: true}, () => formatEventTimeSpan(props.event))
    .otherwise(() => format(props.event.start, START_TIME_FORMAT));
  const isPast = isPastEvent(props.event);

  const leftRadius = props.isStartClipped ? 0 : theme.radii.small;
  const rightRadius = props.isEndClipped ? 0 : theme.radii.small;

  const onClick = () => {
    if (props.event.isReadOnly) {
      return;
    }

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_month_event_click',
      value: {
        eventId: props.event.id,
      },
    });

    props.onClick?.(props.event);
  };

  const onMouseEnter = (mouseEvent: React.MouseEvent) => {
    props.onMouseEnter?.(props.event, mouseEvent);
  };

  const onMouseLeave = () => {
    props.onMouseLeave?.(props.event);
  };

  const eventFilter = isPast ? EVENT_FILTER_WHEN_IS_PAST : 'none';
  const style: MonthEventStyle = {
    '--event-filter': eventFilter,
    backgroundColor: props.event.alternative ? 'transparent' : eventColorHex,
    backgroundImage: props.event.alternative ? repeatingGradient(eventColorHex) : 'none',
    border: props.event.alternative ? `1px solid ${eventColorHex}` : 'none',
    borderRadius: `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`,
    filter: eventFilter,
    transition: 'filter 0.2s ease',
  };

  return (
    <div
      data-testid={suffixTestId('monthEventItem', props)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(monthEventVariants({enableHover: props.enableHover}))}
      style={style}
    >
      <Text
        data-testid={suffixTestId('monthEventItem-title', props)}
        size="xSmall"
        color={props.event.alternative ? 'primary' : 'white'}
        noWrap
        alternative
      >
        {props.event.title}
      </Text>
      <Show when={isNotNil(timeLabel)}>
        {/* The time keeps its width and the title gives way, so it stays readable on a narrow bar. */}
        <Box flexShrink={0}>
          <Text
            data-testid={suffixTestId('monthEventItem-time', props)}
            size="xSmall"
            color={props.event.alternative ? 'primary' : 'white'}
            noWrap
          >
            {timeLabel}
          </Text>
        </Box>
      </Show>
    </div>
  );
}

const START_TIME_FORMAT = 'H:mm';
